import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../utils/cn";
import Icon from '../AppIcon';

const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 touch-manipulation",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/90",
                destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                link: "text-primary underline-offset-4 hover:underline",
                accent: "bg-accent text-accent-foreground hover:bg-accent/90",
                success: "bg-success text-success-foreground hover:bg-success/90",
                warning: "bg-warning text-warning-foreground hover:bg-warning/90",
                danger: "bg-error text-error-foreground hover:bg-error/90",
            },
            size: {
                default: "h-11 px-4 py-2 min-h-[44px]",
                sm: "h-10 rounded-md px-3 min-h-[40px]",
                lg: "h-12 rounded-md px-8 min-h-[48px]",
                icon: "h-11 w-11 min-h-[44px] min-w-[44px]",
                xs: "h-9 rounded-md px-2 text-xs min-h-[36px]",
                xl: "h-14 rounded-md px-10 text-base min-h-[56px]",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

// Optimized motion variants - memoized for performance
const buttonMotionVariants = {
    hover: {
        scale: 0.98,
        transition: { duration: 0.2, ease: "easeInOut" }
    },
    tap: {
        scale: 0.95,
        transition: { duration: 0.1, ease: "easeInOut" }
    }
};

const Button = React.forwardRef(({
    className,
    variant,
    size,
    asChild = false,
    children,
    loading = false,
    iconName = null,
    iconPosition = 'left',
    iconSize = null,
    fullWidth = false,
    disabled = false,
    disableAnimations = false,
    ...props
}, ref) => {
    const Comp = asChild ? Slot : "button";

    // Memoized icon size mapping
    const calculatedIconSize = useMemo(() => {
        const iconSizeMap = {
            xs: 12,
            sm: 14,
            default: 16,
            lg: 18,
            xl: 20,
            icon: 16,
        };
        return iconSize || iconSizeMap?.[size] || 16;
    }, [iconSize, size]);

    // Memoized loading spinner to prevent re-creation
    const LoadingSpinner = useMemo(() => (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    ), []);

    // Memoized icon rendering
    const iconElement = useMemo(() => {
        if (!iconName) return null;
        try {
            return (
                <Icon
                    name={iconName}
                    size={calculatedIconSize}
                    className={cn(
                        children && iconPosition === 'left' && "mr-2",
                        children && iconPosition === 'right' && "ml-2"
                    )}
                />
            );
        } catch {
            return null;
        }
    }, [iconName, calculatedIconSize, children, iconPosition]);

    // Memoized button content
    const buttonContent = useMemo(() => (
        <>
            {loading && LoadingSpinner}
            {iconName && iconPosition === 'left' && iconElement}
            {children}
            {iconName && iconPosition === 'right' && iconElement}
        </>
    ), [loading, LoadingSpinner, iconName, iconPosition, iconElement, children]);

    // Memoized base class name
    const baseClassName = useMemo(() => cn(
        buttonVariants({ variant, size, className }),
        fullWidth && "w-full"
    ), [variant, size, className, fullWidth]);

    // Early return for disabled/loading state with simple button
    if (disableAnimations || disabled || loading) {
        return (
            <Comp
                className={baseClassName}
                ref={ref}
                disabled={disabled || loading}
                {...props}
            >
                {buttonContent}
            </Comp>
        );
    }

    // Animated button with optimized Framer Motion
    const AnimatedButton = motion(Comp);

    return (
        <AnimatedButton
            className={baseClassName}
            ref={ref}
            disabled={disabled || loading}
            variants={buttonMotionVariants}
            whileHover={!disabled && !loading ? "hover" : undefined}
            whileTap={!disabled && !loading ? "tap" : undefined}
            {...props}
        >
            {buttonContent}
        </AnimatedButton>
    );
});

Button.displayName = "Button";
export default React.memo(Button);